import React, { useEffect, useMemo, useState } from "react";
import "../StockInward.css";
import {
  addExpenseMaster,
  deleteExpenseMaster,
  getExpenseMasters,
  updateExpenseMaster,
} from "../storage";
import { formatDisplayDate } from "../utils/dateFormat";

function makeDefaultForm() {
  return { name: "" };
}

export default function ExpenseMaster() {
  const [form, setForm] = useState(() => makeDefaultForm());
  const [expenseMasters, setExpenseMasters] = useState([]);
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState({});

  const refresh = async () => {
    try {
      const list = await getExpenseMasters();
      const next = Array.isArray(list) ? list : [];
      next.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
      setExpenseMasters(next);
    } catch (err) {
      console.error(err);
      alert("Failed to load expense masters.");
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredEntries = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return expenseMasters;
    return expenseMasters.filter((entry) =>
      String(entry?.name || "").toLowerCase().includes(q)
    );
  }, [expenseMasters, query]);

  const resetForm = () => {
    setForm(makeDefaultForm());
    setEditingId("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = String(form.name || "").trim();
    if (!name) {
      alert("Expense name is required.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateExpenseMaster(editingId, { name });
      } else {
        await addExpenseMaster({ name });
      }
      resetForm();
      await refresh();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to save expense master.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (entry) => {
    if (!entry?.id) return;
    setEditingId(entry.id);
    setForm({ name: String(entry.name || "") });
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      // ignore
    }
  };

  const handleDelete = async (entry) => {
    if (!entry?.id) return;
    if (!window.confirm(`Delete expense master "${entry.name || "entry"}"?`)) return;
    setDeleting((prev) => ({ ...prev, [entry.id]: true }));
    try {
      await deleteExpenseMaster(entry.id);
      if (editingId === entry.id) {
        resetForm();
      }
      await refresh();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to delete expense master.");
    } finally {
      setDeleting((prev) => {
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
    }
  };

  return (
    <div className="stock-inward-container" style={{ paddingTop: 0 }}>
      <div className="form-card">
        <div className="card-header">
          <h2 className="card-title">Expense Master</h2>
          <span className="entry-count">{editingId ? "Edit Mode" : "Expense Name Setup"}</span>
        </div>
        <form className="inward-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="field full fl-field">
              <input
                id="expense-master-name"
                className="fl-input input-field"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ name: e.target.value })}
                placeholder=" "
                required
              />
              <label htmlFor="expense-master-name" className="fl-label">
                Expense Name
              </label>
            </div>
          </div>
          <div className="form-actions">
            {editingId ? (
              <>
                <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving}>
                  Cancel Edit
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Updating..." : "Update Expense Name"}
                </button>
              </>
            ) : (
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? "Saving..." : "Add Expense Name"}
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="entries-card">
        <div className="card-header" style={{ borderBottom: "none" }}>
          <h2 className="card-title">Saved Expense Names</h2>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search expense name..."
            className="input-field"
            style={{ maxWidth: 280 }}
          />
        </div>
        <div className="entries-table-container">
          <table className="entries-table" style={{ minWidth: 640 }}>
            <thead>
              <tr>
                <th>Expense Name</th>
                <th style={{ width: 180 }}>Created At</th>
                <th style={{ width: 160 }} className="text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={3} className="empty-state" style={{ padding: 16 }}>
                    No expense names found
                  </td>
                </tr>
              ) : (
                filteredEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.name}</td>
                    <td>{entry.createdAt ? formatDisplayDate(entry.createdAt) : "-"}</td>
                    <td className="text-right">
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => startEdit(entry)}
                          style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                          disabled={!!deleting[entry.id]}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => handleDelete(entry)}
                          style={{ width: 32, height: 32, padding: 0 }}
                          disabled={!!deleting[entry.id]}
                        >
                          x
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
